import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Alert, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { BarChart3, RefreshCw, ClipboardList, Activity, FileText, Clock, Download, LayoutDashboard, Package } from 'lucide-react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import usePermission from '../../Hooks/usePermission';
import { REPORT_VIEW, COST_REPORT_VIEW } from '../../constants/permissions';
import { formatDateTimeForAPI, formatCreatedDate } from '../../constants/utils';
import api from '../../helpers/api';

// Redux actions
import {
  fetchCreatedVsResolved,
  fetchCreatedWorkOrders,
  fetchResolvedWorkOrders,
  setReportDateRange,
  clearWorkOrderList,
  fetchWorkOrdersByType,
  fetchWorkOrdersByTypeList,
  fetchRepeatingReport,
  fetchRepeatingReportList,
  fetchStatusReport,
  fetchStatusReportList,
  fetchPriorityReport,
  fetchPriorityReportList,
  fetchTimeToComplete,
  fetchCompletedWorkOrders,
  fetchMttrWorkOrders,
  fetchOnTimeOverdue,
  fetchOnTimeOverdueList,
  fetchCompletedByUser,
  fetchCompletedByUserList,
  fetchStatusDurationReport,
  fetchRecentActivity,
  fetchRecentActivityMore,
  setRecentActivityFilters,
  fetchWorkOrderCostReport,
} from '../../store/reporting/actions';

import { fetchEscalationEvents } from '../../store/escalation/actions';

// Components
import DateRangeSelector from './DateRangeSelector';
import CreatedVsResolvedWidget from './CreatedVsResolvedWidget';
import WorkOrdersByTypeWidget from './WorkOrdersByTypeWidget';
import RepeatingReportWidget from './RepeatingReportWidget';
import StatusReportWidget from './StatusReportWidget';
import PriorityReportWidget from './PriorityReportWidget';
import TimeToCompleteWidget from './TimeToCompleteWidget';
import OnTimeOverdueWidget from './OnTimeOverdueWidget';
import CompletedByUserWidget from './CompletedByUserWidget';
import WorkOrderSpendWidget from './WorkOrderSpendWidget';
import WorkOrderCostByWoWidget from './WorkOrderCostByWoWidget';
import StatusDurationWidget from './StatusDurationWidget';
import WorkOrderListModal from './WorkOrderListModal';
import TicketDetailsModal from '../../components/TicketDetailsModal';
import AssetModal from '../../components/AssetModal';
import LocationModal from '../../components/LocationModal';
import RecentActivityWidget from './RecentActivityWidget';
import EscalationEventsWidget from './EscalationEventsWidget';
import CurrentStatusWidget from './CurrentStatusWidget';
import MostProblematicWidget from './MostProblematicWidget';
import DowntimeBreakdownWidget from './DowntimeBreakdownWidget';
import TotalDowntimeWidget from './TotalDowntimeWidget';
import DowntimeCostWidget from './DowntimeCostWidget';
import MaintenanceSpendByAssetWidget from './MaintenanceSpendByAssetWidget';
import MtbfWidget from './MtbfWidget';
import AssetsByTypeWidget from './AssetsByTypeWidget';
import AssetsByCriticalityWidget from './AssetsByCriticalityWidget';
import TopAssetsByNbvWidget from './TopAssetsByNbvWidget';
import NbvDepreciationTrendWidget from './NbvDepreciationTrendWidget';
import TopSparePartsUsageWidget from './TopSparePartsUsageWidget';
import MostConsumedPartsWidget from './MostConsumedPartsWidget';
import StockLevelsByCategoryWidget from './StockLevelsByCategoryWidget';
import InventoryValueByCategoryWidget from './InventoryValueByCategoryWidget';
import SparePartUsageTrendWidget from './SparePartUsageTrendWidget';
import VendorSpendingAnalysisWidget from './VendorSpendingAnalysisWidget';
import InventoryAlertsWidget from './InventoryAlertsWidget';
import AssetsRequiringAttentionWidget from './AssetsRequiringAttentionWidget';
import ContractsRequiringAttentionWidget from './ContractsRequiringAttentionWidget';
import ContractDetailModal from '../../components/ContractDetailModal';
import { fetchInventoryReport } from '../../store/reportingInventory/actions';
import ReportingDetailsTab from './ReportingDetailsTab';
import ExportDataTab from './ExportDataTab';
import useReferenceData from '../../Hooks/useReferenceData';
import ReportingFilterBar from './components/ReportingFilterBar';

// Semantic `?tab=` deep-link keys ↔ internal numeric tab IDs. These keys are the
// stable URL contract: numeric IDs can be reshuffled if tabs ever reorder.
const TAB_BY_KEY = {
  workOrders: '1',
  assetHealth: '2',
  inventory: '3',
  reportingDetails: '4',
  recentActivity: '5',
  exportData: '6',
  customDashboards: '7',
};
const KEY_BY_TAB = Object.fromEntries(
  Object.entries(TAB_BY_KEY).map(([k, v]) => [v, k])
);
const DEFAULT_TAB = '1';

const ReportingDashboard = () => {
  const { t } = useTranslation();
  document.title = `${t('shared.reporting', 'Reporting')} | Arcmedix`;

  const PRIORITY_OPTIONS = useMemo(() => [
    { value: 'none', label: t('shared.none', 'None') },
    { value: 'low', label: t('shared.low', 'Low') },
    { value: 'medium', label: t('shared.medium', 'Medium') },
    { value: 'high', label: t('shared.high', 'High') },
  ], [t]);

  const dispatch = useDispatch();
  const { hasPermission, user } = usePermission();

  const organizationId = user?.activeOrganizationId ?? null;

  // Redux state
  const {
    createdVsResolved,
    createdVsResolvedLoading,
    createdVsResolvedError,
    workOrderList,
    workOrderListLoading,
    workOrderListError,
    dateRange,
    workOrdersByType,
    workOrdersByTypeLoading,
    workOrdersByTypeError,
    repeatingReport,
    repeatingReportLoading,
    repeatingReportError,
    statusReport,
    statusReportLoading,
    statusReportError,
    priorityReport,
    priorityReportLoading,
    priorityReportError,
    timeToComplete,
    timeToCompleteLoading,
    timeToCompleteError,
    onTimeOverdue,
    onTimeOverdueLoading,
    onTimeOverdueError,
    completedByUser,
    completedByUserLoading,
    completedByUserError,
    statusDurationReport,
    statusDurationReportLoading,
    statusDurationReportError,
    workOrderCostReport,
    workOrderCostReportLoading,
    workOrderCostReportError,
    recentActivity,
    recentActivityLoading,
    recentActivityLoadingMore,
    recentActivityError,
    recentActivityFilters,
  } = useSelector(state => state.Reporting);

  const { events: escalationEvents, eventsLoading: escalationEventsLoading } =
    useSelector(state => state.Escalation);

  // Local state for modal
  const [modalOpen, setModalOpen] = useState(false);

  // State for ticket details modal (used by WO list and Recent Activity)
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketModalNumber, setTicketModalNumber] = useState(null);

  // State for asset modal
  const [assetModal, setAssetModal] = useState({ isOpen: false, userData: null, loading: false });

  // State for contract detail modal (used by Contracts Requiring Attention on Inventory tab)
  const [contractDetailModal, setContractDetailModal] = useState({ isOpen: false, contractId: null });
  const handleContractClick = useCallback((contractId) => {
    if (!contractId) return;
    setContractDetailModal({ isOpen: true, contractId });
  }, []);

  // State for location details modal
  const [detailsModal, setDetailsModal] = useState({
    isOpen: false, type: null, title: '', mainData: null,
    relatedData: [], loading: false, locationData: null, activeTab: '1'
  });

  // Tab state — initial value comes from the `?tab=` URL param (semantic key)
  // so external deep links (and the Phase 6.5 `/dashboard/*` redirects) land
  // on the correct tab. Falls back to Work Orders for unknown / missing keys.
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const key = searchParams.get('tab');
    return (key && TAB_BY_KEY[key]) || DEFAULT_TAB;
  });

  // Selected report for Reporting Details tab
  const [detailReportKey, setDetailReportKey] = useState('createdVsResolved');

  const updateTab = useCallback((tab) => {
    setActiveTab(tab);
    const key = KEY_BY_TAB[tab];
    if (!key) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) updateTab(tab);
  };

  // Navigate to Reporting Details tab with a specific report
  const handleViewReportDetails = useCallback((reportKey) => {
    setDetailReportKey(reportKey);
    updateTab('4');
  }, [updateTab]);

  // Reference data for filter dropdowns (technicians, locations)
  const { technicians, locations, loading: refDataLoading } = useReferenceData(organizationId, {
    fetchLocations: true,
    fetchTechnicians: true,
    fetchManufacturers: false,
    fetchDepartments: false,
  });

  const technicianOptions = (technicians || []).map(t => ({
    value: t.id,
    label: t.name,
  }));

  const locationOptions = (locations || []).map(l => ({
    value: l.id,
    label: l.locationName,
  }));

  // Harmonized per-tab filter state. Each tab holds its own {criticality, locationId,
  // vendorId, assetId, assetTypeId, contractNumber, workOrderCategoryId, assignedTo,
  // priority, status, dueDateStart, dueDateEnd} subset — only the fields its chips
  // touch are populated.
  const emptyFilters = useMemo(() => ({
    criticality: null, locationId: null, vendorId: null, assetId: null, assetTypeId: null,
    contractNumber: null, workOrderCategoryId: null,
    assignedTo: null, priority: null, status: null,
    dueDateStart: null, dueDateEnd: null,
  }), []);
  const [tabFilters, setTabFilters] = useState({
    workOrders: { ...emptyFilters },
    assetHealth: { ...emptyFilters },
    reportingDetails: { ...emptyFilters },
    recentActivity: { ...emptyFilters },
  });
  const [visibleExtras, setVisibleExtras] = useState({
    workOrders: [],
    assetHealth: [],
    reportingDetails: [],
    recentActivity: [],
  });

  const updateTabFilters = useCallback((key, partial) => {
    setTabFilters(prev => ({ ...prev, [key]: { ...prev[key], ...partial } }));
  }, []);
  const addExtraFilter = useCallback((key, filterKey) => {
    setVisibleExtras(prev => prev[key].includes(filterKey)
      ? prev
      : { ...prev, [key]: [...prev[key], filterKey] });
  }, []);
  const removeExtraFilter = useCallback((key, filterKey) => {
    setVisibleExtras(prev => ({ ...prev, [key]: prev[key].filter(k => k !== filterKey) }));
  }, []);

  // Convenience aliases so widget prop wiring below reads naturally.
  const assetHealthFilters = tabFilters.assetHealth;
  const assetHealthCriticality = assetHealthFilters.criticality;
  const assetHealthLocationId = assetHealthFilters.locationId;
  const assetHealthVendorId = assetHealthFilters.vendorId;
  const assetHealthAssetTypeId = assetHealthFilters.assetTypeId;
  const assetHealthAssetId = assetHealthFilters.assetId;
  const assetHealthContractNumber = assetHealthFilters.contractNumber;
  const assetHealthWorkOrderCategoryId = assetHealthFilters.workOrderCategoryId;

  const [criticalityOptions, setCriticalityOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [assetTypeOptions, setAssetTypeOptions] = useState([]);
  const [assetOptions, setAssetOptions] = useState([]);
  const [contractOptions, setContractOptions] = useState([]);
  const [workOrderCategoryOptions, setWorkOrderCategoryOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  useEffect(() => {
    if (!organizationId) return;
    api.get(`dynamic-enum/find-by-name?name=criticality`)
      .then(({ data: res }) => {
        const values = res.response?.values || [];
        setCriticalityOptions(values.map(v => ({ value: v, label: v })));
      })
      .catch(() => setCriticalityOptions([]));
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    api.get(`vendor/getAllVendors?organizationId=${organizationId}`)
      .then(({ data: res }) => {
        const vendors = res.response || [];
        setVendorOptions(vendors.map(v => ({ value: v.id, label: v.vendorName })));
      })
      .catch(() => setVendorOptions([]));
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    api.get(`organization-asset-type/getAll?organizationId=${organizationId}`)
      .then(({ data: res }) => {
        const types = res.response || [];
        setAssetTypeOptions(types.map(typ => ({ value: typ.id, label: typ.name })));
      })
      .catch(() => setAssetTypeOptions([]));
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    api.get(`reporting/filters/contracts?organizationId=${organizationId}`)
      .then(({ data: res }) => {
        const contracts = res.response || [];
        setContractOptions(contracts.map(c => ({
          value: c.contractNumber,
          label: c.vendorName
            ? `${c.contractNumber} — ${c.vendorName}`
            : c.contractNumber,
        })));
      })
      .catch(() => setContractOptions([]));
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    api.get(`organization-category/getAll?organizationId=${organizationId}`)
      .then(({ data: res }) => {
        const cats = res.response || [];
        setWorkOrderCategoryOptions(cats.map(c => ({ value: c.id, label: c.name })));
      })
      .catch(() => setWorkOrderCategoryOptions([]));
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    api.get(`asset/get-by-organization?organizationId=${organizationId}&count=9999&hierarchy=false`)
      .then(({ data: res }) => {
        // Response is a list of organization groups, each with an `assets` array.
        const groups = Array.isArray(res?.response) ? res.response : [];
        const assets = groups.flatMap(g => g?.assets || []);
        setAssetOptions(assets.map(a => ({
          value: a.id,
          label: a.assetName || a.assetNumber || `#${a.id}`,
        })));
      })
      .catch(() => setAssetOptions([]));
  }, [organizationId]);

  useEffect(() => {
    // Static status options — mirrors the WorkOrder status enum used elsewhere.
    setStatusOptions([
      { value: 'OPEN', label: t('shared.open', 'Open') },
      { value: 'IN_PROGRESS', label: t('shared.in_progress', 'In Progress') },
      { value: 'ON_HOLD', label: t('shared.on_hold', 'On Hold') },
      { value: 'COMPLETED', label: t('shared.completed', 'Completed') },
      { value: 'CANCELLED', label: t('shared.cancelled', 'Cancelled') },
    ]);
  }, [t]);

  // Keep Recent Activity local filter state in sync with the Redux slice so the
  // existing fetch-by-Redux dispatch path keeps working.
  useEffect(() => {
    setTabFilters(prev => ({
      ...prev,
      recentActivity: { ...prev.recentActivity, ...recentActivityFilters },
    }));
  }, [recentActivityFilters]);

  // Check permission
  const canViewReports = hasPermission(REPORT_VIEW);
  const canViewCostReport = hasPermission(COST_REPORT_VIEW);

  // Fetch report data
  const fetchReport = useCallback(() => {
    if (!organizationId || !dateRange.startDate || !dateRange.endDate) return;

    const startDateFormatted = formatDateTimeForAPI(dateRange.startDate);
    const endDateFormatted = formatDateTimeForAPI(dateRange.endDate);

    // The Work Orders tab shows 9 widgets; Reporting Details tab shows the same
    // widgets one at a time, driven by its own filter bar. Whichever of those
    // two tabs the user is viewing, re-fetch with that tab's filters.
    const currentFilters = activeTab === '4' ? tabFilters.reportingDetails : tabFilters.workOrders;

    dispatch(fetchCreatedVsResolved(startDateFormatted, endDateFormatted, organizationId, currentFilters));
    dispatch(fetchWorkOrdersByType(startDateFormatted, endDateFormatted, organizationId, currentFilters));
    dispatch(fetchRepeatingReport(startDateFormatted, endDateFormatted, organizationId, currentFilters));
    dispatch(fetchStatusReport(startDateFormatted, endDateFormatted, organizationId, currentFilters));
    dispatch(fetchPriorityReport(startDateFormatted, endDateFormatted, organizationId, currentFilters));
    dispatch(fetchTimeToComplete(startDateFormatted, endDateFormatted, organizationId, currentFilters));
    dispatch(fetchOnTimeOverdue(startDateFormatted, endDateFormatted, organizationId, currentFilters));
    dispatch(fetchCompletedByUser(startDateFormatted, endDateFormatted, organizationId, currentFilters));
    dispatch(fetchStatusDurationReport(startDateFormatted, endDateFormatted, organizationId, currentFilters));
    if (canViewCostReport) {
      dispatch(fetchWorkOrderCostReport(startDateFormatted, endDateFormatted, organizationId, currentFilters));
    }
  }, [dispatch, organizationId, dateRange.startDate, dateRange.endDate, activeTab, tabFilters.workOrders, tabFilters.reportingDetails, canViewCostReport]);

  // Load data on mount and when date range changes
  useEffect(() => {
    if (canViewReports) {
      fetchReport();
    }
  }, [fetchReport, canViewReports]);

  // Handle date range change
  const handleDateRangeChange = useCallback((startDate, endDate) => {
    dispatch(setReportDateRange(startDate, endDate));
  }, [dispatch]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    fetchReport();
  }, [fetchReport]);

  // Fetch recent activity when tab 4 is active and dependencies change
  const fetchRecentActivityData = useCallback((page = 0) => {
    if (!organizationId || !dateRange.startDate || !dateRange.endDate) return;

    const params = {
      startDate: formatDateTimeForAPI(dateRange.startDate),
      endDate: formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      assignedTo: recentActivityFilters.assignedTo,
      locationId: recentActivityFilters.locationId,
      priority: recentActivityFilters.priority,
      dueDateStart: recentActivityFilters.dueDateStart,
      dueDateEnd: recentActivityFilters.dueDateEnd,
      page,
      pageSize: 15,
    };

    if (page === 0) {
      dispatch(fetchRecentActivity(params));
    } else {
      dispatch(fetchRecentActivityMore(params));
    }
  }, [dispatch, organizationId, dateRange, recentActivityFilters]);

  useEffect(() => {
    if (canViewReports && activeTab === '5') {
      fetchRecentActivityData(0);
      dispatch(fetchEscalationEvents(0, 20));
    }
  }, [fetchRecentActivityData, canViewReports, activeTab, dispatch]);

  // Asset Health (tab 2) and Inventory (tab 3) share the `/inventory` aggregate.
  // Fire when either tab becomes active, and refire on active-org switch so a
  // stale cross-org payload doesn't linger. Widgets render their loading state
  // while the refetch is in flight, so no stale frame flashes through.
  useEffect(() => {
    if (!canViewReports) return;
    if (activeTab !== '2' && activeTab !== '3') return;
    dispatch(fetchInventoryReport());
  }, [canViewReports, activeTab, organizationId, dispatch]);

  // Handle recent activity filter changes
  const handleRecentActivityFiltersChange = useCallback((filterUpdate) => {
    dispatch(setRecentActivityFilters(filterUpdate));
  }, [dispatch]);

  // Handle recent activity load more
  const handleRecentActivityLoadMore = useCallback(() => {
    if (recentActivity) {
      fetchRecentActivityData(recentActivity.page + 1);
    }
  }, [fetchRecentActivityData, recentActivity]);

  // Handle created click (summary card)
  const handleCreatedClick = useCallback(() => {
    if (!organizationId) return;

    dispatch(fetchCreatedWorkOrders(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      null,
      null,
      null
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // Handle resolved click (summary card)
  const handleResolvedClick = useCallback(() => {
    if (!organizationId) return;

    dispatch(fetchResolvedWorkOrders(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      null,
      null,
      null
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // Handle chart point click
  const handleChartPointClick = useCallback((payload, type) => {
    if (!organizationId || !payload) return;

    const fetchAction = type === 'CREATED' ? fetchCreatedWorkOrders : fetchResolvedWorkOrders;

    dispatch(fetchAction(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      formatDateTimeForAPI(payload.periodStart),
      formatDateTimeForAPI(payload.periodEnd),
      payload.periodLabel
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // ---- Work Orders by Type handlers ----

  // Handle preventive click (summary card)
  const handlePreventiveClick = useCallback(() => {
    if (!organizationId) return;

    dispatch(fetchWorkOrdersByTypeList(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      'Preventive',
      null,
      null,
      null
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // Handle reactive click (summary card)
  const handleReactiveClick = useCallback(() => {
    if (!organizationId) return;

    dispatch(fetchWorkOrdersByTypeList(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      'Reactive',
      null,
      null,
      null
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // Handle other click (summary card)
  const handleOtherClick = useCallback(() => {
    if (!organizationId) return;

    dispatch(fetchWorkOrdersByTypeList(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      'Other',
      null,
      null,
      null
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // Handle work type chart bar click
  const handleWorkTypeChartBarClick = useCallback((payload, workType) => {
    if (!organizationId || !payload) return;

    dispatch(fetchWorkOrdersByTypeList(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      workType,
      formatDateTimeForAPI(payload.periodStart),
      formatDateTimeForAPI(payload.periodEnd),
      payload.periodLabel
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // ---- Repeating Report handlers ----

  // Handle non-repeating click (summary card)
  const handleNonRepeatingClick = useCallback(() => {
    if (!organizationId) return;

    dispatch(fetchRepeatingReportList(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      false,
      null,
      null,
      null
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // Handle repeating click (summary card)
  const handleRepeatingClick = useCallback(() => {
    if (!organizationId) return;

    dispatch(fetchRepeatingReportList(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      true,
      null,
      null,
      null
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // Handle repeating chart bar click
  const handleRepeatingChartBarClick = useCallback((payload, isRepeating) => {
    if (!organizationId || !payload) return;

    dispatch(fetchRepeatingReportList(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      isRepeating,
      formatDateTimeForAPI(payload.periodStart),
      formatDateTimeForAPI(payload.periodEnd),
      payload.periodLabel
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // ---- Status Report handlers ----

  // Handle status click (summary card or doughnut segment)
  const handleStatusClick = useCallback((status) => {
    if (!organizationId) return;

    dispatch(fetchStatusReportList(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      status
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // ---- Priority Report handlers ----

  // Handle priority click (summary card or doughnut segment)
  const handlePriorityClick = useCallback((priority) => {
    if (!organizationId) return;

    dispatch(fetchPriorityReportList(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      priority
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // ---- Time to Complete handlers ----

  // Handle Total Hours or AVG Hours click (summary card)
  const handleTotalHoursClick = useCallback(() => {
    if (!organizationId) return;

    dispatch(fetchCompletedWorkOrders(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      null,
      null,
      null
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // Handle MTTR click (summary card)
  const handleMttrClick = useCallback(() => {
    if (!organizationId) return;

    dispatch(fetchMttrWorkOrders(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      null,
      null,
      null
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // Handle Time to Complete chart point click
  const handleTimeToCompleteChartClick = useCallback((payload, type) => {
    if (!organizationId || !payload) return;

    const fetchAction = type === 'MTTR' ? fetchMttrWorkOrders : fetchCompletedWorkOrders;

    dispatch(fetchAction(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      formatDateTimeForAPI(payload.periodStart),
      formatDateTimeForAPI(payload.periodEnd),
      payload.periodLabel
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // ---- On Time vs Overdue handlers ----

  const handleOnTimeClick = useCallback(() => {
    if (!organizationId) return;

    dispatch(fetchOnTimeOverdueList(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      'ON_TIME',
      null
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  const handleOverdueClick = useCallback(() => {
    if (!organizationId) return;

    dispatch(fetchOnTimeOverdueList(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      'OVERDUE',
      null
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  const handleOnTimeOverdueSegmentClick = useCallback((segmentName, isRepeating) => {
    if (!organizationId) return;

    dispatch(fetchOnTimeOverdueList(
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      organizationId,
      segmentName,
      isRepeating
    ));
    setModalOpen(true);
  }, [dispatch, organizationId, dateRange]);

  // ---- Completed by User handlers ----

  const handleUserWeekClick = useCallback((userId, periodIndex) => {
    if (!organizationId || !completedByUser?.periods?.[periodIndex]) return;

    const period = completedByUser.periods[periodIndex];
    dispatch(fetchCompletedByUserList(organizationId, userId, period.start, period.end));
    setModalOpen(true);
  }, [dispatch, organizationId, completedByUser]);

  // Cost-by-WO row click — open the existing ticket details modal by WO number.
  const handleCostRowClick = useCallback((row) => {
    if (!row?.workOrderNumber) return;
    setTicketModalNumber(row.workOrderNumber);
    setTicketModalOpen(true);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    dispatch(clearWorkOrderList());
  }, [dispatch]);

  // Handle work order click (from list modal or recent activity)
  const handleWorkOrderClick = useCallback((workOrder) => {
    if (!workOrder?.ticketNumber) return;
    setTicketModalNumber(workOrder.ticketNumber);
    setTicketModalOpen(true);
  }, []);

  // Handle asset click in WO list modal
  const handleAssetClick = useCallback(async (assetId) => {
    if (!assetId) return;
    setAssetModal({ isOpen: true, userData: null, loading: true });
    try {
      const { data } = await api.get(`asset/get-by-id?id=${assetId}`);
      setAssetModal({ isOpen: true, userData: data.response, loading: false });
    } catch (error) {
      console.error('Error fetching asset details:', error);
      setAssetModal(prev => ({ ...prev, loading: false }));
    }
  }, []);

  // Handle location click in WO list modal
  const handleLocationClick = useCallback(async (locationName) => {
    if (!locationName) return;
    setDetailsModal(prev => ({
      ...prev, isOpen: true, type: 'location',
      title: t('shared.location_details', 'Location Details'),
      mainData: locationName, relatedData: [], loading: true,
      locationData: null, activeTab: '1'
    }));
    try {
      const { data } = await api.get('asset-issues/organization/filtered', {
        params: { organizationLocation: locationName, organizationId }
      });
      setDetailsModal(prev => ({ ...prev, relatedData: data.response || [], loading: false }));
    } catch (error) {
      console.error('Error fetching location details:', error);
      setDetailsModal(prev => ({ ...prev, loading: false }));
    }
  }, [organizationId, t]);

  // Permission denied
  if (!canViewReports) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="warning" className="d-flex align-items-center gap-3">
            <BarChart3 size={24} />
            <div>
              <h5 className="mb-1">{t('shared.access_denied', 'Access Denied')}</h5>
              <p className="mb-0">{t('shared.no_permission_reports', 'You do not have permission to view reports.')}</p>
            </div>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4"
        >
          <h4 className="mb-0 fw-bold">{t('shared.reporting', 'Reporting')}</h4>

          <div className="d-flex align-items-center gap-3">
            <DateRangeSelector
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={handleDateRangeChange}
            />

            <motion.button
              className="btn btn-outline-primary d-flex align-items-center gap-2"
              onClick={handleRefresh}
              disabled={createdVsResolvedLoading || workOrdersByTypeLoading || repeatingReportLoading || statusReportLoading || priorityReportLoading || timeToCompleteLoading || onTimeOverdueLoading || completedByUserLoading || statusDurationReportLoading || workOrderCostReportLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RefreshCw
                size={18}
                className={(createdVsResolvedLoading || workOrdersByTypeLoading || repeatingReportLoading || statusReportLoading || priorityReportLoading || timeToCompleteLoading || onTimeOverdueLoading || completedByUserLoading || statusDurationReportLoading || workOrderCostReportLoading) ? 'spin' : ''}
                style={{
                  animation: (createdVsResolvedLoading || workOrdersByTypeLoading || repeatingReportLoading || statusReportLoading || priorityReportLoading || timeToCompleteLoading || onTimeOverdueLoading || completedByUserLoading || statusDurationReportLoading || workOrderCostReportLoading) ? 'spin 1s linear infinite' : 'none'
                }}
              />
              {t('shared.refresh', 'Refresh')}
            </motion.button>
          </div>
        </motion.div>

        {/* Tabs Navigation */}
        <Nav tabs className="reporting-tabs mb-4 border-bottom">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '1' })}
              onClick={() => toggleTab('1')}
              style={{ cursor: 'pointer' }}
            >
              <ClipboardList size={16} className="me-2" />
              {t('shared.work_orders', 'Work Orders')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '2' })}
              onClick={() => toggleTab('2')}
              style={{ cursor: 'pointer' }}
            >
              <Activity size={16} className="me-2" />
              {t('shared.tab_asset_health', 'Asset Health')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '3' })}
              onClick={() => toggleTab('3')}
              style={{ cursor: 'pointer' }}
            >
              <Package size={16} className="me-2" />
              {t('shared.inventory', 'Inventory')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '4' })}
              onClick={() => toggleTab('4')}
              style={{ cursor: 'pointer' }}
            >
              <FileText size={16} className="me-2" />
              {t('shared.tab_reporting_details', 'Reporting Details')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '5' })}
              onClick={() => toggleTab('5')}
              style={{ cursor: 'pointer' }}
            >
              <Clock size={16} className="me-2" />
              {t('shared.tab_recent_activity', 'Recent Activity')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '6' })}
              onClick={() => toggleTab('6')}
              style={{ cursor: 'pointer' }}
            >
              <Download size={16} className="me-2" />
              {t('shared.tab_export_data', 'Export Data')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '7' })}
              onClick={() => toggleTab('7')}
              style={{ cursor: 'pointer' }}
            >
              <LayoutDashboard size={16} className="me-2" />
              {t('shared.tab_custom_dashboards', 'Custom Dashboards')}
            </NavLink>
          </NavItem>
        </Nav>

        {/* Harmonized per-tab filter bar. Each tab has its own defaults (per MaintainX).
            Extra filters are added via the "+ Add Filter" button. */}
        {activeTab === '1' && (
          <ReportingFilterBar
            tabKey="workOrders"
            filters={tabFilters.workOrders}
            onChange={(p) => updateTabFilters('workOrders', p)}
            visibleExtras={visibleExtras.workOrders}
            onAddFilter={(k) => addExtraFilter('workOrders', k)}
            onRemoveFilter={(k) => removeExtraFilter('workOrders', k)}
            technicianOptions={technicianOptions}
            locationOptions={locationOptions}
            vendorOptions={vendorOptions}
            assetTypeOptions={assetTypeOptions}
            assetOptions={assetOptions}
            criticalityOptions={criticalityOptions}
            priorityOptions={PRIORITY_OPTIONS}
            statusOptions={statusOptions}
            workOrderCategoryOptions={workOrderCategoryOptions}
            contractOptions={contractOptions}
            refDataLoading={refDataLoading}
          />
        )}
        {activeTab === '2' && (
          <ReportingFilterBar
            tabKey="assetHealth"
            filters={tabFilters.assetHealth}
            onChange={(p) => updateTabFilters('assetHealth', p)}
            visibleExtras={visibleExtras.assetHealth}
            onAddFilter={(k) => addExtraFilter('assetHealth', k)}
            onRemoveFilter={(k) => removeExtraFilter('assetHealth', k)}
            technicianOptions={technicianOptions}
            locationOptions={locationOptions}
            vendorOptions={vendorOptions}
            assetTypeOptions={assetTypeOptions}
            assetOptions={assetOptions}
            criticalityOptions={criticalityOptions}
            priorityOptions={PRIORITY_OPTIONS}
            statusOptions={statusOptions}
            workOrderCategoryOptions={workOrderCategoryOptions}
            contractOptions={contractOptions}
            refDataLoading={refDataLoading}
          />
        )}
        {activeTab === '4' && (
          <ReportingFilterBar
            tabKey="reportingDetails"
            filters={tabFilters.reportingDetails}
            onChange={(p) => updateTabFilters('reportingDetails', p)}
            visibleExtras={visibleExtras.reportingDetails}
            onAddFilter={(k) => addExtraFilter('reportingDetails', k)}
            onRemoveFilter={(k) => removeExtraFilter('reportingDetails', k)}
            technicianOptions={technicianOptions}
            locationOptions={locationOptions}
            vendorOptions={vendorOptions}
            assetTypeOptions={assetTypeOptions}
            assetOptions={assetOptions}
            criticalityOptions={criticalityOptions}
            priorityOptions={PRIORITY_OPTIONS}
            statusOptions={statusOptions}
            workOrderCategoryOptions={workOrderCategoryOptions}
            contractOptions={contractOptions}
            refDataLoading={refDataLoading}
          />
        )}
        {activeTab === '5' && (
          <ReportingFilterBar
            tabKey="recentActivity"
            filters={tabFilters.recentActivity}
            onChange={(p) => {
              updateTabFilters('recentActivity', p);
              // Recent Activity still uses Redux for its data fetch; keep in sync.
              handleRecentActivityFiltersChange(p);
            }}
            visibleExtras={visibleExtras.recentActivity}
            onAddFilter={(k) => addExtraFilter('recentActivity', k)}
            onRemoveFilter={(k) => removeExtraFilter('recentActivity', k)}
            technicianOptions={technicianOptions}
            locationOptions={locationOptions}
            vendorOptions={vendorOptions}
            assetTypeOptions={assetTypeOptions}
            assetOptions={assetOptions}
            criticalityOptions={criticalityOptions}
            priorityOptions={PRIORITY_OPTIONS}
            statusOptions={statusOptions}
            workOrderCategoryOptions={workOrderCategoryOptions}
            contractOptions={contractOptions}
            refDataLoading={refDataLoading}
          />
        )}

        {/* Tab Content */}
        <TabContent activeTab={activeTab}>
          {/* Work Orders Tab */}
          <TabPane tabId="1">
            {/* Report Widgets - loading/error states handled by ReportWidget */}
            <div className="row g-4">
              {/* Created vs Resolved Widget */}
              <div className="col-12">
                <CreatedVsResolvedWidget
                  data={createdVsResolved}
                  loading={createdVsResolvedLoading}
                  error={createdVsResolvedError}
                  onCreatedClick={handleCreatedClick}
                  onResolvedClick={handleResolvedClick}
                  onChartPointClick={handleChartPointClick}
                  onDetailsClick={() => handleViewReportDetails('createdVsResolved')}
                />
              </div>

              {/* Work Orders by Type Widget */}
              <div className="col-12">
                <WorkOrdersByTypeWidget
                  data={workOrdersByType}
                  loading={workOrdersByTypeLoading}
                  error={workOrdersByTypeError}
                  onPreventiveClick={handlePreventiveClick}
                  onReactiveClick={handleReactiveClick}
                  onOtherClick={handleOtherClick}
                  onChartBarClick={handleWorkTypeChartBarClick}
                  onDetailsClick={() => handleViewReportDetails('workOrdersByType')}
                />
              </div>

              {/* Repeating Report Widget */}
              <div className="col-12">
                <RepeatingReportWidget
                  data={repeatingReport}
                  loading={repeatingReportLoading}
                  error={repeatingReportError}
                  onNonRepeatingClick={handleNonRepeatingClick}
                  onRepeatingClick={handleRepeatingClick}
                  onChartBarClick={handleRepeatingChartBarClick}
                  onDetailsClick={() => handleViewReportDetails('repeatingReport')}
                />
              </div>

              {/* Status Report Widget */}
              <div className="col-12">
                <StatusReportWidget
                  data={statusReport}
                  loading={statusReportLoading}
                  error={statusReportError}
                  onStatusClick={handleStatusClick}
                  onDetailsClick={() => handleViewReportDetails('statusReport')}
                />
              </div>

              {/* Priority Report Widget */}
              <div className="col-12">
                <PriorityReportWidget
                  data={priorityReport}
                  loading={priorityReportLoading}
                  error={priorityReportError}
                  onPriorityClick={handlePriorityClick}
                  onDetailsClick={() => handleViewReportDetails('priorityReport')}
                />
              </div>

              {/* Time to Complete Widget */}
              <div className="col-12">
                <TimeToCompleteWidget
                  data={timeToComplete}
                  loading={timeToCompleteLoading}
                  error={timeToCompleteError}
                  onTotalHoursClick={handleTotalHoursClick}
                  onAvgHoursClick={handleTotalHoursClick}
                  onMttrClick={handleMttrClick}
                  onChartPointClick={handleTimeToCompleteChartClick}
                  onDetailsClick={() => handleViewReportDetails('timeToComplete')}
                />
              </div>

              {/* On Time vs Overdue Widget */}
              <div className="col-12">
                <OnTimeOverdueWidget
                  data={onTimeOverdue}
                  loading={onTimeOverdueLoading}
                  error={onTimeOverdueError}
                  onOnTimeClick={handleOnTimeClick}
                  onOverdueClick={handleOverdueClick}
                  onSegmentClick={handleOnTimeOverdueSegmentClick}
                  onDetailsClick={() => handleViewReportDetails('onTimeOverdue')}
                />
              </div>

              {/* Work Order Spend (admin-only) — table lives only in Reporting Details. */}
              {canViewCostReport && (
                <div className="col-12">
                  <WorkOrderSpendWidget
                    data={workOrderCostReport}
                    loading={workOrderCostReportLoading}
                    error={workOrderCostReportError}
                    onDetailsClick={() => handleViewReportDetails('workOrderCost')}
                  />
                </div>
              )}

              {/* Completed by User Widget */}
              <div className="col-12">
                <CompletedByUserWidget
                  data={completedByUser}
                  loading={completedByUserLoading}
                  error={completedByUserError}
                  onUserWeekClick={handleUserWeekClick}
                  onDetailsClick={() => handleViewReportDetails('completedByUser')}
                />
              </div>
            </div>
          </TabPane>

          {/* Asset Health Tab — Fleet → Reliability → Cost → Value. Current Status
              sits above the register pies so the operational snapshot leads. */}
          <TabPane tabId="2">
            <div className="row g-4">
              <div className="col-12">
                <CurrentStatusWidget
                  organizationId={organizationId}
                  criticality={assetHealthCriticality}
                  locationId={assetHealthLocationId}
                  vendorId={assetHealthVendorId}
                  assetId={assetHealthAssetId}
                  assetTypeId={assetHealthAssetTypeId}
                  contractNumber={assetHealthContractNumber}
                />
              </div>
              <div className="col-12 col-md-6">
                <AssetsByTypeWidget onAssetClick={handleAssetClick} />
              </div>
              <div className="col-12 col-md-6">
                <AssetsByCriticalityWidget onAssetClick={handleAssetClick} />
              </div>
              <div className="col-12">
                <MostProblematicWidget
                  organizationId={organizationId}
                  dateRange={dateRange}
                  criticality={assetHealthCriticality}
                  locationId={assetHealthLocationId}
                  vendorId={assetHealthVendorId}
                  assetId={assetHealthAssetId}
                  assetTypeId={assetHealthAssetTypeId}
                  contractNumber={assetHealthContractNumber}
                />
              </div>
              <div className="col-12">
                <DowntimeBreakdownWidget
                  organizationId={organizationId}
                  dateRange={dateRange}
                  criticality={assetHealthCriticality}
                  locationId={assetHealthLocationId}
                  vendorId={assetHealthVendorId}
                  assetId={assetHealthAssetId}
                  assetTypeId={assetHealthAssetTypeId}
                  contractNumber={assetHealthContractNumber}
                />
              </div>
              <div className="col-12">
                <TotalDowntimeWidget
                  organizationId={organizationId}
                  dateRange={dateRange}
                  criticality={assetHealthCriticality}
                  locationId={assetHealthLocationId}
                  vendorId={assetHealthVendorId}
                  assetId={assetHealthAssetId}
                  assetTypeId={assetHealthAssetTypeId}
                  contractNumber={assetHealthContractNumber}
                />
              </div>
              <div className="col-12">
                <MtbfWidget
                  organizationId={organizationId}
                  dateRange={dateRange}
                  criticality={assetHealthCriticality}
                  locationId={assetHealthLocationId}
                  vendorId={assetHealthVendorId}
                  assetId={assetHealthAssetId}
                  assetTypeId={assetHealthAssetTypeId}
                  contractNumber={assetHealthContractNumber}
                  workOrderCategoryId={assetHealthWorkOrderCategoryId}
                />
              </div>
              <div className="col-12">
                <DowntimeCostWidget
                  organizationId={organizationId}
                  dateRange={dateRange}
                  criticality={assetHealthCriticality}
                  locationId={assetHealthLocationId}
                  vendorId={assetHealthVendorId}
                  assetId={assetHealthAssetId}
                  assetTypeId={assetHealthAssetTypeId}
                  contractNumber={assetHealthContractNumber}
                />
              </div>
              {canViewCostReport && (
                <div className="col-12">
                  <MaintenanceSpendByAssetWidget
                    organizationId={organizationId}
                    dateRange={dateRange}
                    criticality={assetHealthCriticality}
                    locationId={assetHealthLocationId}
                    vendorId={assetHealthVendorId}
                    assetId={assetHealthAssetId}
                    assetTypeId={assetHealthAssetTypeId}
                    contractNumber={assetHealthContractNumber}
                  />
                </div>
              )}
              {canViewCostReport && (
                <div className="col-12">
                  <TopAssetsByNbvWidget onAssetClick={handleAssetClick} />
                </div>
              )}
              {canViewCostReport && (
                <div className="col-12">
                  <NbvDepreciationTrendWidget />
                </div>
              )}
            </div>
          </TabPane>

          {/* Inventory Tab — spare-parts focus (vendor + alerts land in PR 6c). */}
          <TabPane tabId="3">
            <div className="row g-4">
              <div className="col-12 col-md-6">
                <TopSparePartsUsageWidget />
              </div>
              <div className="col-12 col-md-6">
                <MostConsumedPartsWidget />
              </div>
              <div className="col-12 col-md-6">
                <StockLevelsByCategoryWidget />
              </div>
              {canViewCostReport && (
                <div className="col-12 col-md-6">
                  <InventoryValueByCategoryWidget />
                </div>
              )}
              <div className="col-12">
                <SparePartUsageTrendWidget canViewCost={canViewCostReport} />
              </div>
              {canViewCostReport && (
                <div className="col-12">
                  <VendorSpendingAnalysisWidget />
                </div>
              )}
              <div className="col-12">
                <InventoryAlertsWidget />
              </div>
              <div className="col-12">
                <AssetsRequiringAttentionWidget onAssetClick={handleAssetClick} />
              </div>
              <div className="col-12">
                <ContractsRequiringAttentionWidget onContractClick={handleContractClick} />
              </div>
            </div>
          </TabPane>

          {/* Reporting Details Tab */}
          <TabPane tabId="4">
            <ReportingDetailsTab
              selectedReport={detailReportKey}
              onReportChange={setDetailReportKey}
              organizationId={organizationId}
              dateRange={dateRange}
              filters={tabFilters.reportingDetails}
              reportData={{
                createdVsResolved,
                workOrdersByType,
                repeatingReport,
                statusReport,
                priorityReport,
                timeToComplete,
                onTimeOverdue,
                completedByUser,
                statusDurationReport,
                workOrderCost: workOrderCostReport,
              }}
              reportLoading={{
                createdVsResolved: createdVsResolvedLoading,
                workOrdersByType: workOrdersByTypeLoading,
                repeatingReport: repeatingReportLoading,
                statusReport: statusReportLoading,
                priorityReport: priorityReportLoading,
                timeToComplete: timeToCompleteLoading,
                onTimeOverdue: onTimeOverdueLoading,
                completedByUser: completedByUserLoading,
                statusDurationReport: statusDurationReportLoading,
                workOrderCost: workOrderCostReportLoading,
              }}
              reportErrors={{
                createdVsResolved: createdVsResolvedError,
                workOrdersByType: workOrdersByTypeError,
                repeatingReport: repeatingReportError,
                statusReport: statusReportError,
                priorityReport: priorityReportError,
                timeToComplete: timeToCompleteError,
                onTimeOverdue: onTimeOverdueError,
                completedByUser: completedByUserError,
                statusDurationReport: statusDurationReportError,
                workOrderCost: workOrderCostReportError,
              }}
              handlers={{
                onCreatedClick: handleCreatedClick,
                onResolvedClick: handleResolvedClick,
                onChartPointClick: handleChartPointClick,
                onPreventiveClick: handlePreventiveClick,
                onReactiveClick: handleReactiveClick,
                onOtherClick: handleOtherClick,
                onWorkTypeChartBarClick: handleWorkTypeChartBarClick,
                onNonRepeatingClick: handleNonRepeatingClick,
                onRepeatingClick: handleRepeatingClick,
                onRepeatingChartBarClick: handleRepeatingChartBarClick,
                onStatusClick: handleStatusClick,
                onPriorityClick: handlePriorityClick,
                onTotalHoursClick: handleTotalHoursClick,
                onAvgHoursClick: handleTotalHoursClick,
                onMttrClick: handleMttrClick,
                onTimeToCompleteChartClick: handleTimeToCompleteChartClick,
                onOnTimeClick: handleOnTimeClick,
                onOverdueClick: handleOverdueClick,
                onOnTimeOverdueSegmentClick: handleOnTimeOverdueSegmentClick,
                onUserWeekClick: handleUserWeekClick,
                onCostRowClick: handleCostRowClick,
              }}
            />
          </TabPane>

          {/* Recent Activity Tab */}
          <TabPane tabId="5">
            <div className="row g-4">
              <div className="col-12">
                <RecentActivityWidget
                  data={recentActivity}
                  loading={recentActivityLoading}
                  loadingMore={recentActivityLoadingMore}
                  error={recentActivityError}
                  onLoadMore={handleRecentActivityLoadMore}
                  onWorkOrderClick={handleWorkOrderClick}
                />
              </div>
              <div className="col-12">
                <EscalationEventsWidget
                  events={escalationEvents}
                  loading={escalationEventsLoading}
                />
              </div>
            </div>
          </TabPane>

          {/* Export Data Tab */}
          <TabPane tabId="6">
            <ExportDataTab organizationId={organizationId} />
          </TabPane>

          {/* Custom Dashboards Tab */}
          <TabPane tabId="7">
            <div className="text-center py-5 text-muted">
              <LayoutDashboard size={48} className="mb-3" />
              <h5>{t('shared.tab_custom_dashboards', 'Custom Dashboards')}</h5>
              <p>{t('reporting.dashboard.custom_dashboards_coming_soon', 'Coming soon - Create and customize your own dashboards.')}</p>
            </div>
          </TabPane>
        </TabContent>

        {/* Work Order List Modal */}
        <WorkOrderListModal
          isOpen={modalOpen}
          toggle={handleModalClose}
          workOrderList={workOrderList}
          loading={workOrderListLoading}
          error={workOrderListError}
          onWorkOrderClick={handleWorkOrderClick}
          onAssetClick={handleAssetClick}
          onLocationClick={handleLocationClick}
        />

        {/* Ticket Details Modal (full WO view, same as Reactive page) */}
        <TicketDetailsModal
          isOpen={ticketModalOpen}
          toggle={() => { setTicketModalOpen(false); setTicketModalNumber(null); }}
          ticketNumber={ticketModalNumber}
          formatCreatedDate={formatCreatedDate}
        />

        {/* Asset Modal */}
        <AssetModal
          isOpen={assetModal.isOpen}
          toggle={() => setAssetModal(prev => ({ ...prev, isOpen: false }))}
          userData={assetModal.userData}
          loading={assetModal.loading}
          formatCreatedDate={formatCreatedDate}
          onTicketClick={(ticketNumber) => handleWorkOrderClick({ ticketNumber })}
        />

        {/* Contract Detail Modal (Inventory tab → Contracts Requiring Attention) */}
        <ContractDetailModal
          isOpen={contractDetailModal.isOpen}
          toggle={() => setContractDetailModal({ isOpen: false, contractId: null })}
          contractId={contractDetailModal.contractId}
        />

        {/* Location Details Modal */}
        <LocationModal
          isOpen={detailsModal.isOpen}
          toggle={() => setDetailsModal(prev => ({ ...prev, isOpen: false }))}
          title={detailsModal.title}
          mainData={detailsModal.mainData}
          relatedData={detailsModal.relatedData}
          loading={detailsModal.loading}
          formatCreatedDate={formatCreatedDate}
          onTicketClick={(ticketNumber) => handleWorkOrderClick({ ticketNumber })}
          locationData={detailsModal.locationData}
        />
      </div>

      {/* Spin animation CSS */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin {
            animation: spin 1s linear infinite;
          }
          .reporting-tabs {
            gap: 0.5rem;
          }
          .reporting-tabs .nav-link {
            color: #6c757d;
            background-color: #fff;
          }
          .reporting-tabs .nav-link.active {
            color: #3980c0;
            background-color: #fff;
            border-bottom: 2px solid #3980c0;
          }
        `}
      </style>
    </div>
  );
};

export default ReportingDashboard;
